import React, { useState } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    useTheme,
    Grid,
    IconButton,
    Tabs,
    Tab,
} from '@material-ui/core'
import BlockIcon from '@material-ui/icons/Block'
import CloseIcon from '@material-ui/icons/Close'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import FiberSmartRecordIcon from '@material-ui/icons/FiberSmartRecord'

import { InternalInvoice, PaymentForm } from '../../../models'
import {
    AddLineItemRequest,
    EditLineItemRequest,
    RemoveLineItemRequest,
} from '../../../hooks/useBilling'
import { InvoiceStatusBadge } from '../InvoiceStatusBadge'
import { InvoiceSummary } from './InvoiceSummary'
import { RecordPaymentDialog } from './RecordPaymentDialog'
import { VoidInvoiceDialog } from './VoidInvoiceDialog'
import { PaymentHistoryTable } from './PaymentHistoryTable'
import { FollowUpHistoryTable } from './FollowUpHistoryTable'
import { PropertiesTable } from './PropertiesTable'
import { LineItemsTable } from './LineItemsTable'
import { Container } from '../../Container'

interface InvoiceDetailModalProps {
    invoice: InternalInvoice | null
    onClose: () => void
    onAddPayment: (payment: PaymentForm) => void
    onVoidInvoice: (reason: string) => void
    onVoidPayment: (paymentId: string, reason: string) => void
    onMarkReviewed: (invoiceId: string) => void
    onUpdateInvoiceDates?: (
        invoiceId: string,
        issue_date: string,
        due_date: string,
    ) => void
    onAddLineItem?: (data: AddLineItemRequest) => Promise<void>
    onEditLineItem?: (data: EditLineItemRequest) => Promise<void>
    onRemoveLineItem?: (data: RemoveLineItemRequest) => Promise<void>
    isAddLineItemLoading?: boolean
    isEditLineItemLoading?: boolean
    isRemoveLineItemLoading?: boolean
    viewOnly?: boolean
}

export const InternalInvoiceModal: React.FC<InvoiceDetailModalProps> = ({
    invoice,
    onClose,
    onAddPayment,
    onVoidInvoice,
    onVoidPayment,
    onMarkReviewed,
    onUpdateInvoiceDates,
    onAddLineItem,
    onEditLineItem,
    onRemoveLineItem,
    isAddLineItemLoading = false,
    isEditLineItemLoading = false,
    isRemoveLineItemLoading = false,
    viewOnly = false,
}) => {
    const theme = useTheme()
    const [showVoidSection, setShowVoidSection] = useState(false)
    const [voidReason, setVoidReason] = useState('')
    const [voidingPaymentId, setVoidingPaymentId] = useState<string | null>(
        null,
    )
    const open = !!invoice
    const [showRecordPayment, setShowRecordPayment] = useState(false)
    const [selectedTab, setSelectedTab] = useState(0)

    const handleTabChange = (
        event: React.ChangeEvent<{}>,
        newValue: number,
    ) => {
        setSelectedTab(newValue)
    }

    const handleVoidPayment = () => {
        if (voidingPaymentId && voidReason) {
            onVoidPayment(voidingPaymentId, voidReason)
            setVoidingPaymentId(null)
            setVoidReason('')
        }
    }

    if (!invoice) return null

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                style: {
                    height: '90vh',
                    maxHeight: '90vh',
                },
            }}
        >
            {/* Header */}
            <Container
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: 'row',
                    flex: 1,
                    padding: 2,
                }}
            >
                {/* Left: Invoice title and status badge */}
                <Box
                    display="flex"
                    flexDirection="column"
                    padding={2}
                    minWidth={220}
                >
                    <Typography variant="h6">
                        Invoice #{invoice.invoice_number}
                        {invoice.voided && (
                            <Typography color="error" variant="subtitle1">
                                VOIDED - {invoice.void_reason}
                            </Typography>
                        )}
                    </Typography>
                    <InvoiceStatusBadge
                        status={invoice.status}
                        voided={invoice.voided}
                        reviewed={invoice.reviewed}
                    />
                </Box>

                {/* Center: Action buttons */}
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    flex={1}
                >
                    {/* Shared button style for uniform size */}
                    {(() => {
                        const actionButtonStyle = {
                            margin: '0 8px',
                            minWidth: 200,
                            minHeight: 48,
                            fontWeight: 600,
                            fontSize: '1rem',
                            boxShadow: '0px 2px 6px rgba(0,0,0,0.07)',
                        }
                        return (
                            <>
                                {/* Void Invoice */}
                                {!viewOnly &&
                                    !invoice.voided &&
                                    !showVoidSection && (
                                        <Button
                                            color="default"
                                            variant="contained"
                                            startIcon={
                                                <BlockIcon
                                                    style={{
                                                        fontWeight: 'bold',
                                                    }}
                                                />
                                            } // Placeholder icon
                                            onClick={() =>
                                                setShowVoidSection(true)
                                            }
                                            style={actionButtonStyle}
                                        >
                                            Void Invoice
                                        </Button>
                                    )}
                                {/* Record Payment */}
                                {!viewOnly &&
                                    !invoice.voided &&
                                    invoice.status !== 'PAID' &&
                                    invoice.status !== 'VOIDED' && (
                                        <Button
                                            color="default"
                                            variant="contained"
                                            startIcon={
                                                <FiberSmartRecordIcon
                                                    style={{
                                                        fontWeight: 'bold',
                                                    }}
                                                />
                                            } // Placeholder icon
                                            onClick={() =>
                                                setShowRecordPayment(true)
                                            }
                                            style={actionButtonStyle}
                                        >
                                            Record Payment
                                        </Button>
                                    )}
                                {/* Ready to Send */}
                                {!viewOnly &&
                                    !invoice.voided &&
                                    invoice.status === 'DRAFT' &&
                                    invoice.reviewed === false && (
                                        <Button
                                            color="default"
                                            variant="contained"
                                            startIcon={
                                                <CheckCircleIcon
                                                    style={{
                                                        fontWeight: 'bold',
                                                    }}
                                                />
                                            }
                                            onClick={() => {
                                                onMarkReviewed(invoice.id)
                                            }}
                                            style={actionButtonStyle}
                                        >
                                            Ready to Send
                                        </Button>
                                    )}
                            </>
                        )
                    })()}
                </Box>

                {/* Right: Close button */}
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    minWidth={120}
                >
                    <IconButton onClick={onClose}>
                        <CloseIcon fontSize="large" />
                    </IconButton>
                </Box>
            </Container>

            <DialogContent style={{ height: 'calc(90vh - 120px)' }}>
                <Grid container spacing={3} style={{ height: '100%' }}>
                    {/* Left Column - Invoice Details */}
                    <Grid
                        item
                        xs={12}
                        md={6}
                        style={{ height: '100%', overflow: 'auto' }}
                    >
                        {/* Invoice Summary */}
                        <Box mb={3}>
                            <InvoiceSummary
                                invoice={invoice}
                                onUpdateInvoiceDates={onUpdateInvoiceDates}
                                viewOnly={viewOnly}
                            />
                        </Box>

                        <Tabs
                            style={{
                                width: '100%',
                                backgroundColor: theme.palette.grey[300],
                                marginBottom: theme.spacing(2),
                            }}
                            value={selectedTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab
                                label="Line Items"
                                style={{ textTransform: 'none' }}
                            />
                            <Tab
                                label="Payments"
                                style={{ textTransform: 'none' }}
                            />
                            <Tab
                                label="Email Logs"
                                style={{ textTransform: 'none' }}
                            />
                            <Tab
                                label="Properties"
                                style={{ textTransform: 'none' }}
                            />
                        </Tabs>

                        {/* Line Items */}
                        {selectedTab === 0 &&
                            onAddLineItem &&
                            onEditLineItem &&
                            onRemoveLineItem && (
                                <LineItemsTable
                                    invoice={invoice}
                                    onAddLineItem={onAddLineItem}
                                    onEditLineItem={onEditLineItem}
                                    onRemoveLineItem={onRemoveLineItem}
                                    viewOnly={viewOnly}
                                    isAddLineItemLoading={isAddLineItemLoading}
                                    isEditLineItemLoading={
                                        isEditLineItemLoading
                                    }
                                    isRemoveLineItemLoading={
                                        isRemoveLineItemLoading
                                    }
                                />
                            )}

                        {/* Payment History */}
                        {selectedTab === 1 && (
                            <PaymentHistoryTable
                                invoice={invoice}
                                viewOnly={viewOnly}
                                onVoidPayment={onVoidPayment}
                            />
                        )}

                        {/* Follow up history */}
                        {selectedTab === 2 && (
                            <FollowUpHistoryTable
                                messages={invoice.email_logs}
                            />
                        )}

                        {/* Properties */}
                        {selectedTab === 3 && (
                            <PropertiesTable
                                properties={invoice.billing_group.properties}
                            />
                        )}

                        <RecordPaymentDialog
                            onAddPayment={onAddPayment}
                            invoice={invoice}
                            open={showRecordPayment}
                            onClose={() => setShowRecordPayment(false)}
                        />
                    </Grid>

                    {/* Right Column - PDF Viewer */}
                    <Grid item xs={12} md={6} style={{ height: '100%' }}>
                        {invoice.invoice ? (
                            <Box
                                height="100%"
                                display="flex"
                                flexDirection="column"
                            >
                                <Box flex={1} style={{ minHeight: 0 }}>
                                    <iframe
                                        src={invoice.invoice}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                            backgroundColor:
                                                theme.palette.background.paper,
                                        }}
                                        title="Invoice PDF"
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                height="100%"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor={theme.palette.background.paper}
                                borderRadius={theme.shape.borderRadius}
                            >
                                <Typography color="textSecondary">
                                    No PDF available for this invoice
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                {/* Void Invoice Dialog */}
                {!viewOnly && showVoidSection && (
                    <VoidInvoiceDialog
                        voidReason={voidReason}
                        setVoidReason={setVoidReason}
                        open={showVoidSection}
                        onClose={() => setShowVoidSection(false)}
                        onVoidInvoice={onVoidInvoice}
                        invoiceId={invoice.id}
                    />
                )}

                {/* Record Payment Dialog*/}
                <RecordPaymentDialog
                    onAddPayment={onAddPayment}
                    invoice={invoice}
                    open={showRecordPayment}
                    onClose={() => setShowRecordPayment(false)}
                />
            </DialogContent>

            <DialogActions>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    px={2}
                >
                    <Box>{!viewOnly && showVoidSection && <></>}</Box>
                </Box>
            </DialogActions>

            {/* Void Payment Dialog - Only render if not viewOnly */}
            {!viewOnly && (
                <Dialog
                    open={!!voidingPaymentId}
                    onClose={() => setVoidingPaymentId(null)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Void Payment</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="Reason for voiding"
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            variant="outlined"
                            required
                            autoFocus
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setVoidingPaymentId(null)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleVoidPayment}
                            color="secondary"
                            disabled={!voidReason}
                            variant="contained"
                        >
                            Void Payment
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Dialog>
    )
}
